/**
 * The Reports area — DESIGN.md §10.2, §13.1 (`/reports/:dashboard`).
 *
 * Four fixed dashboards, each a list of widget config objects (§10.2), one
 * shared filter bar, and a drill-down: "every widget count click-throughs to a
 * pre-filtered card list". The drill-down is a dialog rather than a separate
 * screen, but it is URL-addressable — the filters ride in the query string, so
 * "the 41 cards behind that bar" is a link you can paste into a card.
 */
import { useMemo, useState } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { BarChart3 } from 'lucide-react';
import { defaultViewConfig, type Filter, type TaskSummary } from '@slate/shared';
import { EmptyState } from '@/components/ui/empty-state';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { TaskList } from '@/components/cards/task-list';
import { ReportFilterBar } from '@/components/reports/filter-bar';
import { ReportWidget } from '@/components/reports/widget';
import { DASHBOARDS, dashboardBySlug, widgetKey } from '@/features/reports/dashboards';
import { defaultReportRange, type ReportFilters } from '@/features/reports/api';
import { listRows, useTaskList } from '@/features/tasks/api';
import { useCurrentUser } from '@/lib/auth';
import { cn } from '@/lib/utils';

/**
 * Filter state lives in the URL so a filtered dashboard is shareable (and so
 * switching tabs keeps the filters). `drill` carries the drill-down's filters
 * as JSON — the same `Filter[]` the query engine takes, which is what makes a
 * drill-down "a pre-filtered card list" rather than a second query language.
 */
const searchSchema = z.object({
  from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  to: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  projects: z.array(z.string()).optional(),
  assignees: z.array(z.string()).optional(),
  groups: z.array(z.string()).optional(),
  drill: z.string().optional(),
  drillLabel: z.string().optional(),
});

export const Route = createFileRoute('/reports/$dashboard')({
  validateSearch: searchSchema,
  component: DashboardPage,
});

function DashboardPage(): JSX.Element {
  const { dashboard: slug } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const dashboard = dashboardBySlug(slug);

  // The viewer's timezone: the server dates every row in it, so a default
  // range computed in UTC dropped today's cards for ten hours a day (#87).
  const me = useCurrentUser();
  const fallback = useMemo(() => defaultReportRange(me.timezone), [me.timezone]);
  const filters: ReportFilters = {
    range: { from: search.from ?? fallback.from, to: search.to ?? fallback.to },
    projectIds: search.projects ?? [],
    assigneeIds: search.assignees ?? [],
    groupIds: search.groups ?? [],
  };

  const setFilters = (next: ReportFilters): void => {
    void navigate({
      search: (previous) => ({
        ...previous,
        from: next.range.from,
        to: next.range.to,
        projects: next.projectIds.length > 0 ? next.projectIds : undefined,
        assignees: next.assigneeIds.length > 0 ? next.assigneeIds : undefined,
        groups: next.groupIds.length > 0 ? next.groupIds : undefined,
      }),
    });
  };

  const openDrilldown = (drilldown: Filter[], label: string): void => {
    void navigate({
      search: (previous) => ({
        ...previous,
        drill: JSON.stringify(drilldown),
        drillLabel: label,
      }),
    });
  };

  const closeDrilldown = (): void => {
    void navigate({
      search: (previous) => ({ ...previous, drill: undefined, drillLabel: undefined }),
    });
  };

  if (!dashboard) {
    return (
      <div className="mx-auto w-full max-w-6xl p-4 md:p-6">
        <EmptyState
          icon={<BarChart3 className="size-8" />}
          title="No such dashboard"
          description="Pick one of the four dashboards above."
        />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-6xl p-4 md:p-6">
      <h1 className="text-xl font-semibold tracking-tight">Reports</h1>
      {/* Same construction as the Admin tabs: rule on the wrapper, strip
          pulled over it, so the scroll container never has a stray pixel of
          vertical overflow to grow a scrollbar for. */}
      <div className="mt-4 border-b">
        <nav
          aria-label="Dashboards"
          className="scrollbar-thin -mb-px flex gap-1 overflow-x-auto overflow-y-hidden"
        >
          {DASHBOARDS.map((item) => (
            <Link
              key={item.slug}
              to="/reports/$dashboard"
              params={{ dashboard: item.slug }}
              search={(previous) => ({ ...previous, drill: undefined, drillLabel: undefined })}
              className={cn(
                'whitespace-nowrap border-b-2 px-3 py-2 text-sm transition-colors',
                item.slug === slug
                  ? 'border-primary text-foreground font-medium'
                  : 'text-muted-foreground hover:text-foreground border-transparent',
              )}
            >
              {item.title}
            </Link>
          ))}
        </nav>
      </div>

      <p className="text-muted-foreground mb-4 mt-4 text-sm">{dashboard.description}</p>

      <ReportFilterBar value={filters} onChange={setFilters} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {dashboard.widgets.map((widget) => (
          <ReportWidget
            key={widgetKey(widget)}
            widget={widget}
            filters={filters}
            onDrilldown={openDrilldown}
          />
        ))}
      </div>

      <DrilldownDialog
        raw={search.drill}
        label={search.drillLabel ?? 'Cards'}
        onClose={closeDrilldown}
      />
    </div>
  );
}

/**
 * §10.2's click-through. It builds a ViewConfig from the drill-down filters and
 * hands it to the same engine every list uses (§7.1) — the numbers and the
 * cards behind them cannot disagree, because they are the same query.
 */
function DrilldownDialog({
  raw,
  label,
  onClose,
}: {
  raw: string | undefined;
  label: string;
  onClose: () => void;
}): JSX.Element | null {
  const navigate = useNavigate();
  const [failed, setFailed] = useState(false);

  const config = useMemo(() => {
    if (!raw) return null;
    try {
      const filters = JSON.parse(raw) as Filter[];
      return defaultViewConfig({
        filters,
        sort: [{ field: 'number', dir: 'desc' }],
        // The drill-down's own filters decide what "completed" means here; a
        // "cards completed this week" click must not have them filtered out.
        showCompleted: true,
      });
    } catch {
      setFailed(true);
      return null;
    }
  }, [raw]);

  const query = useTaskList(config ?? defaultViewConfig({}), { enabled: Boolean(config) });
  const { items, total } = listRows(query.data);

  if (!raw) return null;

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent className="max-h-[85dvh] w-[min(60rem,95vw)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {label}
            <span className="text-muted-foreground ml-2 text-sm font-normal">
              {total} {total === 1 ? 'card' : 'cards'}
            </span>
          </DialogTitle>
        </DialogHeader>

        {failed ? (
          <EmptyState title="That link is malformed" description="Click the chart again." />
        ) : (
          <TaskList
            items={items}
            total={total}
            dense
            isLoading={query.isPending}
            isFetchingNextPage={query.isFetchingNextPage}
            hasNextPage={Boolean(query.hasNextPage)}
            fetchNextPage={() => void query.fetchNextPage()}
            onOpen={(task: TaskSummary) =>
              void navigate({ to: '/card/$number', params: { number: String(task.number) } })
            }
            emptyTitle="No cards behind that number"
            emptyDescription="The report and the list ran at slightly different moments; try again."
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
